/**
 * Централизованная система ошибок для Chat Service
 * Основана на лучших практиках error handling с типизацией
 */

// ===== ERROR CODES =====

export const ChatErrorCodes = {
  // Authentication errors
  UNAUTHORIZED: "UNAUTHORIZED",
  USER_NOT_FOUND: "USER_NOT_FOUND",

  // Chat creation errors
  INVALID_ROLE_COMBINATION: "INVALID_ROLE_COMBINATION",
  CANNOT_CHAT_WITH_SELF: "CANNOT_CHAT_WITH_SELF",
  CHAT_CREATION_FAILED: "CHAT_CREATION_FAILED",

  // Message errors
  EMPTY_MESSAGE: "EMPTY_MESSAGE",
  MESSAGE_SEND_FAILED: "MESSAGE_SEND_FAILED",
  INVALID_CHAT_ACCESS: "INVALID_CHAT_ACCESS",

  // Database errors
  DATABASE_ERROR: "DATABASE_ERROR",
  NETWORK_ERROR: "NETWORK_ERROR",

  // Real-time errors
  REALTIME_CONNECTION_FAILED: "REALTIME_CONNECTION_FAILED",
  REALTIME_SUBSCRIPTION_FAILED: "REALTIME_SUBSCRIPTION_FAILED",

  // Generic errors
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
  VALIDATION_ERROR: "VALIDATION_ERROR",
} as const;

export type ChatErrorCode = keyof typeof ChatErrorCodes;

// ===== ERROR CONTEXT INTERFACE =====

export interface ErrorContext {
  /** Function or method where error occurred */
  method?: string;
  /** Chat ID if applicable */
  chatId?: string;
  /** User ID if applicable */
  userId?: string;
  /** Email/username if applicable */
  email?: string;
  /** Channel key for realtime subscriptions */
  channelKey?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
  /** Timestamp when error occurred */
  timestamp?: string;
  /** HTTP status code if applicable */
  statusCode?: number;
}

// ===== MAIN ERROR CLASS =====

/**
 * Централизованный класс ошибок для Chat Service
 *
 * Features:
 * - Типизированные error codes
 * - Structured error context
 * - Support for error chaining (cause)
 * - JSON serializable
 * - Stack trace preservation
 *
 * @example
 * ```typescript
 * // Basic usage
 * throw new ChatServiceError(
 *   'UNAUTHORIZED',
 *   'User not authenticated',
 *   { method: 'listMyChats', userId: '123' }
 * );
 *
 * // With cause chaining
 * try {
 *   await supabase.from('chats').select();
 * } catch (error) {
 *   throw new ChatServiceError(
 *     'DATABASE_ERROR',
 *     'Failed to fetch chats from database',
 *     { method: 'listMyChats' },
 *     error
 *   );
 * }
 * ```
 */
export class ChatServiceError extends Error {
  public readonly code: ChatErrorCode;
  public readonly context?: ErrorContext;
  public readonly cause?: Error | unknown;
  public readonly timestamp: string;

  constructor(
    code: ChatErrorCode,
    message: string,
    context?: ErrorContext,
    cause?: Error | unknown,
  ) {
    super(message);

    this.name = "ChatServiceError";
    this.code = code;
    this.context = {
      ...context,
      timestamp: context?.timestamp || new Date().toISOString(),
    };
    this.cause = cause;
    this.timestamp = new Date().toISOString();

    // Preserve stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ChatServiceError);
    }
  }

  /**
   * Convert error to JSON for logging/reporting
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      context: this.context,
      timestamp: this.timestamp,
      stack: this.stack,
      cause:
        this.cause instanceof Error
          ? {
              name: this.cause.name,
              message: this.cause.message,
              stack: this.cause.stack,
            }
          : this.cause,
    };
  }

  /**
   * Check if error is retryable based on error code
   */
  isRetryable(): boolean {
    const retryableCodes: ChatErrorCode[] = [
      "NETWORK_ERROR",
      "DATABASE_ERROR",
      "REALTIME_CONNECTION_FAILED",
      "REALTIME_SUBSCRIPTION_FAILED",
    ];

    return retryableCodes.includes(this.code);
  }

  /**
   * Get user-friendly error message for UI display
   */
  getUserMessage(): string {
    const userMessages: Record<ChatErrorCode, string> = {
      UNAUTHORIZED: "Please log in to continue",
      USER_NOT_FOUND: "User not found",
      INVALID_ROLE_COMBINATION:
        "Cannot create chat between users with the same role",
      CANNOT_CHAT_WITH_SELF: "Cannot create chat with yourself",
      CHAT_CREATION_FAILED: "Failed to create chat",
      EMPTY_MESSAGE: "Message cannot be empty",
      MESSAGE_SEND_FAILED: "Failed to send message",
      INVALID_CHAT_ACCESS: "You do not have access to this chat",
      DATABASE_ERROR: "Database connection error. Please try again.",
      NETWORK_ERROR: "Network error. Please check your connection.",
      REALTIME_CONNECTION_FAILED: "Failed to connect to real-time updates",
      REALTIME_SUBSCRIPTION_FAILED: "Failed to subscribe to real-time updates",
      UNKNOWN_ERROR: "An unexpected error occurred",
      VALIDATION_ERROR: "Invalid input data",
    };

    return userMessages[this.code] || this.message;
  }
}

// ===== UTILITY FUNCTIONS =====

/**
 * Convert unknown error to ChatServiceError
 */
export function toChatServiceError(
  error: unknown,
  defaultCode: ChatErrorCode = "UNKNOWN_ERROR",
  context?: ErrorContext,
): ChatServiceError {
  if (error instanceof ChatServiceError) {
    return error;
  }

  if (error instanceof Error) {
    // Handle Supabase/PostgreSQL errors
    if ("code" in error) {
      const pgError = error as { code: string; message: string };

      // Map PostgreSQL error codes
      if (pgError.code === "PGRST116") {
        return new ChatServiceError(
          "USER_NOT_FOUND",
          "User not found",
          context,
          error,
        );
      }

      if (pgError.code === "23505") {
        return new ChatServiceError(
          "VALIDATION_ERROR",
          "Duplicate entry",
          context,
          error,
        );
      }

      return new ChatServiceError(
        "DATABASE_ERROR",
        pgError.message,
        context,
        error,
      );
    }

    // Handle fetch/network errors
    if (error.message.includes("fetch") || error.message.includes("network")) {
      return new ChatServiceError(
        "NETWORK_ERROR",
        error.message,
        context,
        error,
      );
    }

    return new ChatServiceError(defaultCode, error.message, context, error);
  }

  // Handle string errors
  if (typeof error === "string") {
    return new ChatServiceError(defaultCode, error, context);
  }

  // Handle unknown error types
  return new ChatServiceError(
    defaultCode,
    "An unexpected error occurred",
    context,
    error,
  );
}

/**
 * Create error for authentication failures
 */
export function createAuthError(context?: ErrorContext): ChatServiceError {
  return new ChatServiceError(
    "UNAUTHORIZED",
    "User not authenticated",
    context,
  );
}

/**
 * Create error for validation failures
 */
export function createValidationError(
  message: string,
  context?: ErrorContext,
): ChatServiceError {
  return new ChatServiceError("VALIDATION_ERROR", message, context);
}

/**
 * Create error for database operations
 */
export function createDatabaseError(
  message: string,
  context?: ErrorContext,
  cause?: Error,
): ChatServiceError {
  return new ChatServiceError("DATABASE_ERROR", message, context, cause);
}

/**
 * Create error for real-time operations
 */
export function createRealtimeError(
  message: string,
  context?: ErrorContext,
  cause?: Error,
): ChatServiceError {
  return new ChatServiceError(
    "REALTIME_CONNECTION_FAILED",
    message,
    context,
    cause,
  );
}
